import { FP_ORG_LOGO_ASSET_ID } from "@forgepoint/shared";
import { useQuoteDocumentContext } from "./quoteDocumentContext";
import type { FpCommonProps } from "./types";

interface FpImageProps extends FpCommonProps {
  assetId: string | null;
  height: string | null;
  width: string | null;
}

/**
 * Renders `<fp-image>`: the resolved asset, or a placeholder box when unresolved.
 * Height and width are CSS dimensions, so `120px` and `50%` size the image alike.
 * An unset height or width renders as `auto`, so setting one dimension scales
 * the image on its aspect ratio. Container props land on the wrapper box, which
 * is what makes `align` and `padding` on an image take effect.
 *
 * An unresolved asset draws the placeholder in the editor only: on a
 * customer's quote or PDF a missing asset has to degrade to nothing rather
 * than to a dashed grey box.
 * @param props - The asset id, height, width, and common fp-* props
 * @returns The image element, the editor's placeholder box, or null
 */
export function FpImage({
  assetId,
  height,
  width,
  className,
  style,
  "data-fp-node": dataFpNode,
}: FpImageProps) {
  const { assetUrls, editable } = useQuoteDocumentContext();
  const src = assetId !== null ? (assetUrls[assetId] ?? null) : null;
  const size = { height: height ?? "auto", width: width ?? "auto" };
  const isOrgLogo = assetId === FP_ORG_LOGO_ASSET_ID;

  if (src === null && !editable) {
    return null;
  }

  return (
    <span
      className={className ?? undefined}
      style={{ display: "block", ...style }}
      data-fp-node={dataFpNode}
    >
      {src ? (
        <img
          src={src}
          alt={assetId ?? ""}
          className="inline-block max-w-full object-contain"
          style={size}
        />
      ) : (
        <span
          className="inline-flex items-center justify-center min-w-[170px] px-4 bg-slate-100 border border-dashed border-slate-300 rounded-md text-slate-500 text-[11px]"
          style={size}
        >
          {isOrgLogo ? "Organization logo" : assetId ? "image" : "no asset"}
        </span>
      )}
    </span>
  );
}
